import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ShieldCheck, Cpu, FileCheck2, Bell, Globe2, Activity, ArrowRight, Sparkles, Beaker, Building2, FileDown, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import PublicPreviewWidget from "@/components/PublicPreviewWidget";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WHITEPAPER_URL = `${API}/whitepaper/vitaqms-unified-platform.pdf`;

export default function Landing() {
  const [lead, setLead] = useState({
    name: "", email: "", company: "", role_title: "", phone: "", interest: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const submitLead = async (e) => {
    e.preventDefault();
    if (!lead.name || !lead.email || !lead.company) {
      return toast.error("Name, work email and company are required");
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/marketing/leads`, { ...lead, source: "landing" });
      setSubmitted(true);
      // Trigger the whitepaper download in a new tab.
      window.open(WHITEPAPER_URL, "_blank", "noopener");
      toast.success("Thanks — the white paper is opening now. Check your inbox for early-access next steps.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not submit — please email hello@vitaqms.com");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Nav */}
      <nav className="vq-nav sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" data-testid="nav-logo" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-slate-900 rounded flex items-center justify-center">
              <ShieldCheck className="w-4 h-4 text-white" />
            </div>
            <span className="font-display font-extrabold text-xl tracking-tight">VitaQMS</span>
            <span className="hidden sm:inline text-xs text-slate-500 font-mono ml-2">by PerCen360</span>
          </Link>
          <div className="flex items-center gap-6">
            <a href="#pain-points" className="hidden md:block text-sm text-slate-600 hover:text-slate-900 font-medium">Why VitaQMS</a>
            <a href="#features" className="hidden md:block text-sm text-slate-600 hover:text-slate-900 font-medium">Features</a>
            <a href="#early-access" className="hidden md:block text-sm text-slate-600 hover:text-slate-900 font-medium">White paper</a>
            <Link to="/login" data-testid="nav-login" className="text-sm text-slate-600 hover:text-slate-900 font-medium">Sign in</Link>
            <a href="#early-access" data-testid="nav-cta-request">
              <Button className="bg-slate-900 hover:bg-[#0055FF] text-white rounded-md">Request early access</Button>
            </a>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 vq-grid-bg opacity-70 pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 pt-20 pb-24 grid grid-cols-1 lg:grid-cols-12 gap-10 relative">
          <div className="lg:col-span-7 vq-fade-up">
            <div className="inline-flex items-center gap-2 vq-overline mb-6">
              <span className="w-1.5 h-1.5 bg-[#0055FF] rounded-full" />
              First-in-class · Unified QMS + Clinical · Beta by invitation
            </div>
            <h1 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] text-slate-900">
              The world&apos;s first<br />
              <span className="text-[#0055FF]">unified QMS and<br />
              clinical evidence</span><br />
              platform for medical devices.
            </h1>
            <p className="mt-8 text-lg text-slate-600 max-w-xl leading-relaxed">
              One workspace for ISO 13485, EU MDR/IVDR, FDA QMSR, MDSAP, IEC 62304, ISO 14971, ISO 14155, the EU AI Act &mdash; and every clinical trial, PMCF, patient-advisory and design-control record they touch. Built for SaMD, AI-enabled and traditional devices, from first idea to post-market.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <a href="#early-access" data-testid="hero-cta-early-access">
                <Button className="bg-slate-900 hover:bg-[#0055FF] text-white h-12 px-6 rounded-md font-semibold">
                  Request early access <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </a>
              <a href="#early-access" data-testid="hero-cta-whitepaper">
                <Button variant="outline" className="h-12 px-6 rounded-md font-semibold border-slate-300">
                  <FileDown className="w-4 h-4 mr-2" /> Download the white paper
                </Button>
              </a>
            </div>
            <p className="mt-4 text-xs text-slate-500 max-w-xl leading-relaxed" data-testid="hero-disclaimer-short">
              VitaQMS is a software tool that assists with templates, workflows and evidence traceability. The customer is solely responsible for the validation, customization, implementation and effectiveness of their Quality Management System and for meeting all regulatory requirements.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-6 text-xs font-mono text-slate-500">
              <span>ISO 13485</span>
              <span className="w-1 h-1 bg-slate-300 rounded-full" />
              <span>FDA QMSR</span>
              <span className="w-1 h-1 bg-slate-300 rounded-full" />
              <span>EU MDR/IVDR</span>
              <span className="w-1 h-1 bg-slate-300 rounded-full" />
              <span>MDSAP</span>
              <span className="w-1 h-1 bg-slate-300 rounded-full" />
              <span>ISO 14971 · 14155 · 62304 · 62366</span>
              <span className="w-1 h-1 bg-slate-300 rounded-full" />
              <span>EU AI Act</span>
            </div>
          </div>

          {/* Hero side card — classification preview */}
          <div className="lg:col-span-5 vq-fade-up" style={{ animationDelay: "0.15s" }}>
            <div className="vq-card p-6 shadow-xl shadow-slate-900/5">
              <div className="flex items-center justify-between mb-4">
                <span className="vq-overline">Live Classification</span>
                <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-50 font-mono">Ready</Badge>
              </div>
              <div className="font-mono text-xs text-slate-500 mb-1">VitaCyt-POC-v1.2</div>
              <div className="font-display text-2xl font-bold text-slate-900 mb-6">Point-of-Care Cell Analyzer</div>

              <div className="grid grid-cols-2 gap-3 mb-6">
                {[
                  { m: "FDA", c: "Class II", p: "510(k)" },
                  { m: "EU MDR", c: "Class IIb", p: "Rule 11" },
                  { m: "India", c: "Class C", p: "CDSCO" },
                  { m: "MDSAP", c: "Applicable", p: "Multi-market" },
                ].map((x) => (
                  <div key={x.m} className="border border-slate-200 rounded-md p-3">
                    <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500">{x.m}</div>
                    <div className="font-display font-bold text-slate-900 text-sm mt-0.5">{x.c}</div>
                    <div className="text-xs text-slate-500 mt-0.5">{x.p}</div>
                  </div>
                ))}
              </div>

              <div className="border-t border-slate-200 pt-4">
                <div className="flex justify-between items-baseline text-sm">
                  <span className="text-slate-600">Compliance Score</span>
                  <span className="font-display font-extrabold text-2xl text-slate-900">98<span className="text-slate-400 text-base">/100</span></span>
                </div>
                <div className="mt-2 w-full h-1.5 bg-slate-100 rounded-full overflow-hidden">
                  <div className="h-full bg-[#0055FF] rounded-full" style={{ width: "98%" }} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Public preview widget — no signup */}
      <PublicPreviewWidget />

      {/* Pain points — why the market needs this */}
      <section id="pain-points" className="max-w-7xl mx-auto px-6 py-24">
        <div className="max-w-3xl mb-14">
          <div className="vq-overline mb-3">The problem we solve</div>
          <h2 className="font-display font-bold text-3xl sm:text-4xl lg:text-5xl tracking-tight text-slate-900">
            Fragmented regulations. Siloed systems. Every audit is a fire drill.
          </h2>
          <p className="mt-6 text-lg text-slate-600 leading-relaxed">
            Most medical-device teams run their QMS in one tool, their eCRF in another, their risk file in a spreadsheet and their clinical evidence in a shared drive. It leaves gaps that Notified Bodies and the FDA find every single time.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            { t: "Regulatory fragmentation", d: "ISO 13485, FDA QMSR (2026), EU MDR/IVDR, MDSAP, IEC 62304, ISO 14971, ISO 14155, IEC 62366 — plus the EU AI Act and cybersecurity for AI-enabled devices. Each with different evidence templates." },
            { t: "Siloed QMS + clinical data", d: "Duplicate entry, version conflicts, incomplete Design History Files, findings that never make it back into the risk file. Clinical results end up outside the QMS." },
            { t: "Audit fire drills", d: "Days lost pulling exports from four systems, praying the versions line up. One-click evidence packages? Not without a platform that knows every regulation." },
            { t: "AI & SaMD governance gap", d: "EU AI Act high-risk, FDA predetermined change control plans, GMLP, IEC 62304 lifecycle — most QMS tools weren't built for continuously-learning models." },
            { t: "Patient co-design still bolted on", d: "Regulators increasingly expect structured patient advisory input, preference studies, and human-factors validation. Word docs and inbox threads don't satisfy that." },
            { t: "Change control breaks traceability", d: "A change in a component or a firmware release should surface every affected clinical study, risk control and jurisdictional filing — automatically." },
          ].map((it) => (
            <div key={it.t} className="vq-card p-6">
              <div className="w-8 h-8 rounded bg-rose-100 text-rose-700 flex items-center justify-center mb-4">
                <Sparkles className="w-4 h-4" />
              </div>
              <div className="font-display font-bold text-lg text-slate-900 mb-2">{it.t}</div>
              <p className="text-sm text-slate-600 leading-relaxed">{it.d}</p>
            </div>
          ))}
        </div>
        <div className="mt-12 vq-card p-8 bg-slate-900 text-white">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-center">
            <div className="lg:col-span-2">
              <div className="vq-overline text-slate-400 mb-2">First-in-class</div>
              <div className="font-display font-bold text-2xl mb-2">
                VitaQMS is the world&apos;s first unified QMS + clinical + patient co-design platform.
              </div>
              <p className="text-slate-300">
                One workspace. One traceability spine. Every regulation. Every evidence package generated with the click of a button, mapped to the exact clause a reviewer will ask about.
              </p>
            </div>
            <a href="#early-access" className="lg:justify-self-end">
              <Button className="bg-white text-slate-900 hover:bg-[#0055FF] hover:text-white h-12 px-6 font-semibold">
                Get the white paper <FileDown className="w-4 h-4 ml-2" />
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="max-w-7xl mx-auto px-6 py-24">
        <div className="max-w-2xl mb-16">
          <div className="vq-overline mb-3">The Platform</div>
          <h2 className="font-display font-bold text-3xl sm:text-4xl lg:text-5xl tracking-tight text-slate-900">
            AI-assisted workflows that give your quality team a running start.
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
          {/* Feature 1 — large */}
          <div className="md:col-span-4 vq-card p-8 vq-card-hover">
            <Cpu className="w-8 h-8 text-[#0055FF] mb-6" />
            <h3 className="font-display font-bold text-xl text-slate-900 mb-2">AI-assisted classification</h3>
            <p className="text-slate-600 leading-relaxed">
              Enter your intended use. VitaQMS suggests candidate device classifications for FDA, EU MDR, India CDSCO and MDSAP with rationale and applicable rules — for review and confirmation by your regulatory team. Powered by Claude Sonnet 4.5.
            </p>
            <div className="mt-6 flex flex-wrap gap-2 font-mono text-xs">
              {["510(k)", "De Novo", "PMA", "MDR Rule 11", "IVDR", "IEC 62304"].map((t) => (
                <span key={t} className="px-2 py-1 border border-slate-200 rounded text-slate-600">{t}</span>
              ))}
            </div>
          </div>

          {/* Feature 2 */}
          <div className="md:col-span-2 vq-card p-8 vq-card-hover">
            <FileCheck2 className="w-8 h-8 text-slate-900 mb-6" />
            <h3 className="font-display font-bold text-xl text-slate-900 mb-2">SOP & DHF templates</h3>
            <p className="text-slate-600 text-sm leading-relaxed">Ready-to-review SOP scaffolds, risk file structure, and DHF sections mapped to ISO 13485 and 21 CFR Part 820 — customize under your quality process.</p>
          </div>

          {/* Feature 3 */}
          <div className="md:col-span-2 vq-card p-8 vq-card-hover">
            <Bell className="w-8 h-8 text-[#EAB308] mb-6" />
            <h3 className="font-display font-bold text-xl text-slate-900 mb-2">Regulatory alerts</h3>
            <p className="text-slate-600 text-sm leading-relaxed">Live polling of FDA, EU EMA and CDSCO feeds. Severity-tagged. Guidance signals for your team&apos;s review.</p>
          </div>

          {/* Feature 4 — large */}
          <div className="md:col-span-4 vq-card p-8 vq-card-hover">
            <Globe2 className="w-8 h-8 text-[#0055FF] mb-6" />
            <h3 className="font-display font-bold text-xl text-slate-900 mb-2">India + US billing, HIPAA / GDPR-ready</h3>
            <p className="text-slate-600 leading-relaxed">
              Stripe-powered subscriptions with tax handling in 80+ countries. Optional 2FA. Session security with cookies + JWT. No card data on our servers.
            </p>
            <div className="mt-6 flex flex-wrap gap-3 text-xs font-mono text-slate-500">
              <span>PCI DSS</span><span>•</span>
              <span>SOC 2 track</span><span>•</span>
              <span>HIPAA-ready</span><span>•</span>
              <span>GDPR-aligned</span>
            </div>
          </div>

          {/* Feature 5 — image */}
          <div className="md:col-span-6 vq-card overflow-hidden vq-card-hover">
            <div className="grid grid-cols-1 md:grid-cols-2">
              <div className="p-8">
                <Beaker className="w-8 h-8 text-slate-900 mb-6" />
                <h3 className="font-display font-bold text-2xl text-slate-900 mb-2">Connected to VitaCyt POC</h3>
                <p className="text-slate-600 leading-relaxed">
                  Import device data, sensitivity/specificity metrics and clinical validation results directly from the VitaCyt point-of-care platform. One-click seed for classification review and QMS scaffolding.
                </p>
                <Link to="/register" className="inline-flex items-center gap-2 mt-6 text-sm font-semibold text-[#0055FF]">
                  Try the demo project <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
              <div className="min-h-[280px] bg-cover bg-center" style={{
                backgroundImage: "url('https://images.pexels.com/photos/9574516/pexels-photo-9574516.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940')"
              }} />
            </div>
          </div>
        </div>

        {/* Medium disclaimer — recommended placement */}
        <div className="mt-16 vq-card p-6 border-l-4 border-l-[#0055FF]" data-testid="hero-disclaimer-medium">
          <div className="vq-overline mb-2">Important note</div>
          <p className="text-sm text-slate-700 leading-relaxed">
            VitaQMS provides AI-assisted templates, document generation support, and workflows to help medical device companies develop elements of their Quality Management System. All AI-generated content must be reviewed and approved by qualified personnel under your own quality processes.
          </p>
          <p className="text-sm text-slate-700 leading-relaxed mt-3">
            You remain solely responsible for system validation, process suitability, training, and compliance with FDA, EU MDR, MDSAP, CDSCO, and all other applicable regulations. Use of this platform does not guarantee regulatory approval or inspection readiness.
          </p>
        </div>
      </section>

      {/* Ecosystem */}
      <section id="ecosystem" className="bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-6 py-20 grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <div className="vq-overline text-slate-400 mb-3">The Ecosystem</div>
            <h2 className="font-display font-bold text-3xl sm:text-4xl tracking-tight">
              Part of the PerCen360<br />compliance ecosystem.
            </h2>
            <p className="mt-6 text-slate-300 leading-relaxed">
              VitaQMS connects with VitaCen.Health and VitaCyt data pipelines to give you a single audit-ready compliance thread — from bench to submission.
            </p>
          </div>
          <div className="md:col-span-7 grid grid-cols-1 sm:grid-cols-3 gap-3">
            {[
              { name: "PerCen360", tag: "Compliance Engine", icon: Activity },
              { name: "VitaCen.Health", tag: "Clinical Programs", icon: Building2 },
              { name: "VitaCyt", tag: "POC Data Layer", icon: Beaker },
            ].map((x) => (
              <div key={x.name} className="border border-white/10 rounded-lg p-6 hover:bg-white/5 transition-colors">
                <x.icon className="w-6 h-6 text-[#0055FF] mb-4" />
                <div className="font-display font-bold text-lg">{x.name}</div>
                <div className="text-xs font-mono text-slate-400 mt-1">{x.tag}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 py-24 text-center">
        <Sparkles className="w-10 h-10 mx-auto text-[#0055FF] mb-6" />
        <h2 className="font-display font-extrabold text-3xl sm:text-4xl lg:text-5xl tracking-tight text-slate-900 max-w-3xl mx-auto">
          Give your quality team a running start.
        </h2>
        <p className="mt-6 text-slate-600 max-w-xl mx-auto">
          Start free. Upgrade to Pro when your workflows are ready. India + US billing supported.
        </p>
        <div className="mt-10 flex justify-center gap-4">
          <Link to="/register" data-testid="footer-cta-start">
            <Button className="bg-slate-900 hover:bg-[#0055FF] text-white h-12 px-8 rounded-md font-semibold">
              Start free wizard
            </Button>
          </Link>
          <Link to="/pricing" data-testid="footer-cta-pricing">
            <Button variant="outline" className="h-12 px-8 rounded-md font-semibold border-slate-300">
              See pricing
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer with long disclaimer */}
      {/* Early access — lead capture + white paper */}
      <section id="early-access" className="bg-slate-900 text-white py-24">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-6">
            <div className="vq-overline text-slate-400 mb-3">Early access · beta by invitation</div>
            <h2 className="font-display font-bold text-3xl sm:text-4xl lg:text-5xl tracking-tight">
              Request your access code &amp; download the white paper.
            </h2>
            <p className="mt-6 text-lg text-slate-300 leading-relaxed">
              VitaQMS is invite-only during the beta so we can work closely with founding customers. Tell us who you are, get the full white paper immediately, and we&apos;ll follow up within one business day with an access code.
            </p>
            <div className="mt-8 space-y-3 text-sm text-slate-300">
              {[
                "Unified QMS + clinical + patient co-design in one workspace",
                "Global harmonisation engine: MDR/IVDR, FDA QMSR, MDSAP, AI Act",
                "Direct line to the founding team during your evaluation",
                "Founder pricing locked in for your first year",
              ].map((b) => (
                <div key={b} className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                  <span>{b}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="lg:col-span-6">
            {!submitted ? (
              <form onSubmit={submitLead}
                    className="bg-white text-slate-900 rounded-lg p-8 shadow-2xl"
                    data-testid="landing-lead-form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs font-semibold">Full name *</Label>
                    <Input required data-testid="lead-name" value={lead.name}
                           onChange={(e) => setLead({ ...lead, name: e.target.value })}
                           className="mt-1 h-11" placeholder="Dr Elena Voss" />
                  </div>
                  <div>
                    <Label className="text-xs font-semibold">Work email *</Label>
                    <Input required type="email" data-testid="lead-email" value={lead.email}
                           onChange={(e) => setLead({ ...lead, email: e.target.value })}
                           className="mt-1 h-11" placeholder="elena@medtech.com" />
                  </div>
                  <div>
                    <Label className="text-xs font-semibold">Company *</Label>
                    <Input required data-testid="lead-company" value={lead.company}
                           onChange={(e) => setLead({ ...lead, company: e.target.value })}
                           className="mt-1 h-11" placeholder="MedTech Diagnostics" />
                  </div>
                  <div>
                    <Label className="text-xs font-semibold">Role / title</Label>
                    <Input data-testid="lead-role" value={lead.role_title}
                           onChange={(e) => setLead({ ...lead, role_title: e.target.value })}
                           className="mt-1 h-11" placeholder="Head of Regulatory Affairs" />
                  </div>
                  <div className="sm:col-span-2">
                    <Label className="text-xs font-semibold">Phone <span className="text-slate-400 font-normal">(optional but helps us prioritise)</span></Label>
                    <Input data-testid="lead-phone" value={lead.phone}
                           onChange={(e) => setLead({ ...lead, phone: e.target.value })}
                           className="mt-1 h-11" placeholder="+44 20 …" />
                  </div>
                  <div className="sm:col-span-2">
                    <Label className="text-xs font-semibold">What&apos;s your biggest QMS / clinical pain point?</Label>
                    <Textarea data-testid="lead-interest" rows={3} value={lead.interest}
                              onChange={(e) => setLead({ ...lead, interest: e.target.value })}
                              className="mt-1" placeholder="e.g. Managing MDR + FDA QMSR + PMCF for our AI-enabled SaMD" />
                  </div>
                </div>
                <Button type="submit" disabled={submitting} data-testid="lead-submit"
                        className="mt-6 w-full h-12 bg-slate-900 hover:bg-[#0055FF] text-white font-semibold">
                  {submitting
                    ? "Submitting…"
                    : <><FileDown className="w-4 h-4 mr-2" /> Get the white paper &amp; request access</>}
                </Button>
                <p className="mt-3 text-[10px] font-mono text-slate-500 text-center">
                  We use your details only to send access codes and product updates. No sharing, ever.
                </p>
              </form>
            ) : (
              <div className="bg-white text-slate-900 rounded-lg p-8 shadow-2xl text-center" data-testid="landing-lead-thanks">
                <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-4" />
                <div className="font-display font-bold text-2xl mb-2">Thanks — your white paper is opening now.</div>
                <p className="text-sm text-slate-600 mb-6">
                  If the download didn&apos;t start,{" "}
                  <a href={WHITEPAPER_URL} target="_blank" rel="noopener noreferrer" className="text-[#0055FF] underline">click here</a>.
                </p>
                <p className="text-xs font-mono text-slate-500">
                  We&apos;ll email your beta access code within one business day.
                </p>
              </div>
            )}
          </div>
        </div>
      </section>

      <footer className="border-t border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto px-6 py-10">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 mb-8">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 bg-slate-900 rounded flex items-center justify-center">
                <ShieldCheck className="w-3 h-3 text-white" />
              </div>
              <span className="font-display font-bold">VitaQMS.com</span>
              <span className="text-slate-400 text-sm ml-2 font-mono">• by PerCen360</span>
            </div>
            <div className="text-sm text-slate-500 font-mono">
              VitaCen.Health • Privacy • Compliance
            </div>
          </div>
          <div className="border-t border-slate-200 pt-6 text-[11px] leading-relaxed text-slate-500 max-w-4xl" data-testid="hero-disclaimer-long">
            <p className="mb-2 font-semibold text-slate-700">Legal disclaimer</p>
            <p className="mb-2">
              VitaQMS is a software tool that provides AI-assisted templates, document generation support, and workflows to help medical device companies develop elements of their Quality Management System. It is not a substitute for regulatory advice, and it does not perform any regulatory certification, submission, or approval.
            </p>
            <p className="mb-2">
              All AI-generated content, including but not limited to device classifications, SOPs, risk assessments, Design History File (DHF) scaffolds, effectiveness checks, gap analyses, and inspection portal outputs, is provided for informational purposes only and must be reviewed, validated, and approved by qualified personnel operating under the customer&apos;s own quality management processes.
            </p>
            <p className="mb-2">
              The customer is solely responsible for the validation, customization, implementation, training, ongoing maintenance, and effectiveness of their Quality Management System, and for meeting the requirements of FDA 21 CFR Part 820 / Part 11, EU MDR / IVDR, ISO 13485, ISO 14971, IEC 62304, IEC 62366, India CDSCO Medical Device Rules, MDSAP, GDPR, HIPAA, and any and all other applicable regulations. Use of this platform does not guarantee regulatory approval, market authorization, audit outcomes, or inspection readiness.
            </p>
            <p>
              VitaQMS © 2026. All names, trademarks, and case examples (including &ldquo;VitaCyt POC&rdquo; and &ldquo;PerCen360&rdquo;) are demonstration content unless otherwise noted.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
